<template>
	<div>
		<div class="d-flex align-items-center gap-2 mb-2">
			<span class="fw-semibold">{{
				lead ? `Lead ${lead.id}` : "New Lead"
			}}</span>
			<span
				v-if="lead"
				class="badge"
				:class="
					lead.state === 'closed' ? 'text-bg-secondary' : 'text-bg-success'
				"
			>
				{{ lead.state }}
			</span>
			<button
				v-if="!lead"
				class="btn btn-sm btn-outline-secondary ms-auto"
				@click="reset"
			>
				Clear
			</button>
		</div>

		<div class="mb-2">
			<label class="form-label small mb-1">Name</label>
			<input
				v-model="form.name"
				class="form-control form-control-sm"
				placeholder="e.g. Neglected pool — Oak Ridge"
			/>
		</div>
		<div class="row g-2 mb-2">
			<div class="col-6">
				<label class="form-label small mb-1">Priority</label>
				<select v-model="form.priority" class="form-select form-select-sm">
					<option value="high">High</option>
					<option value="normal">Normal</option>
					<option value="low">Low</option>
				</select>
			</div>
			<div class="col-6">
				<label class="form-label small mb-1">Due</label>
				<input
					v-model="form.due"
					type="date"
					class="form-control form-control-sm"
				/>
			</div>
		</div>
		<div class="mb-2">
			<label class="form-label small mb-1"
				>Rationale &amp; field guidance</label
			>
			<textarea
				v-model="form.rationale"
				class="form-control form-control-sm"
				rows="3"
			></textarea>
		</div>

		<div v-if="lead" class="mb-2">
			<label class="form-label small mb-1">Attached Signals</label>
			<div
				v-for="link in lead.signals"
				:key="link.signal_id"
				class="small d-flex gap-2"
			>
				<span class="fw-semibold">S-{{ link.signal_id }}</span>
				<span class="text-muted">{{ link.effect }}</span>
				<span class="badge text-bg-light">{{ link.role }}</span>
			</div>
		</div>

		<div class="d-flex align-items-center justify-content-between mb-1">
			<label class="form-label small mb-0">{{
				lead ? "Staged for attach" : "Evidence"
			}}</label>
			<span class="small text-muted">{{ staged.length }} staged</span>
		</div>
		<div v-if="staged.length === 0" class="text-muted small mb-2">
			Select Signals in the ledger, then use Attach.
		</div>
		<div
			v-for="signal in staged"
			:key="signal.id"
			class="d-flex gap-2 align-items-center small mb-1"
		>
			<span class="fw-semibold">S-{{ signal.id }}</span>
			<span class="text-muted">{{ signal.provenance_class }}</span>
			<select
				v-model="signalEffects[signal.id]"
				class="form-select form-select-sm ms-auto"
				style="width: 8rem"
			>
				<option value="promotive">promotive</option>
				<option value="suppressive">suppressive</option>
				<option value="contextual">contextual</option>
			</select>
		</div>

		<div class="d-flex align-items-center justify-content-between mt-2 mb-1">
			<label class="form-label small mb-0">H3 cells</label>
			<span class="small text-muted"
				>{{ draftCells.length }} at res {{ resolution }}</span
			>
		</div>
		<div
			class="d-flex flex-wrap gap-1 mb-2"
			style="max-height: 7rem; overflow: auto"
		>
			<span v-for="cell in draftCells" :key="cell" class="badge text-bg-light">
				{{ cell }}
				<button
					class="btn btn-sm p-0 ms-1"
					title="Remove cell"
					@click="emit('remove-cell', cell)"
				>
					<i class="bi bi-x"></i>
				</button>
			</span>
		</div>
		<div class="form-text mb-2">
			Click the map to add a cell; click a cell chip to remove it.
		</div>

		<div v-if="lead" class="mb-2">
			<label class="form-label small mb-1">Linked Assignments</label>
			<div v-if="lead.assignments.length === 0" class="text-muted small">
				None yet.
			</div>
			<div v-for="a in lead.assignments" :key="a.id" class="small">
				#{{ a.id }} · {{ a.kind ?? "work" }} · {{ a.state }}
				<span v-if="a.route_id"> · route {{ a.route_id }}</span>
			</div>
		</div>

		<div class="d-flex gap-2">
			<button
				class="btn btn-sm btn-primary"
				:disabled="saving || !canSave || draftBlockers.length > 0"
				@click="save"
			>
				{{ lead ? "Save Lead" : "Create Lead" }}
			</button>
			<button
				v-if="lead && lead.state === 'open'"
				class="btn btn-sm btn-outline-secondary ms-auto"
				:disabled="saving"
				@click="emit('request-evidence')"
			>
				Close…
			</button>
		</div>

		<!-- Why the forge would refuse this draft, stated before the round trip
		     rather than as a 400 afterwards. The link opens the Signal's own
		     details beside this form, so the fix happens without losing the
		     draft. -->
		<div
			v-if="draftBlockers.length > 0"
			class="alert alert-warning py-2 px-2 mt-2 mb-0"
			role="status"
		>
			<div class="fw-semibold small mb-1">
				Before this {{ lead ? "Lead can change" : "Lead can be created" }}
			</div>
			<ul class="small mb-0 ps-3">
				<li v-for="blocker in draftBlockers" :key="blocker.key">
					{{ blocker.message }}
					<button
						v-if="blocker.review !== undefined"
						type="button"
						class="btn btn-link btn-sm p-0 ms-1 align-baseline"
						@click="emit('review', blocker.review)"
					>
						Review Signal {{ blocker.review }}
					</button>
				</li>
			</ul>
		</div>

		<div v-if="lead" class="mt-2 d-flex gap-2 align-items-center">
			<select
				v-model="attachRole"
				class="form-select form-select-sm"
				style="width: 9rem"
			>
				<option value="context">context</option>
				<option value="composition">composition</option>
			</select>
			<button
				class="btn btn-sm btn-outline-primary"
				:disabled="staged.length === 0 || saving"
				@click="attach"
			>
				Attach {{ staged.length }} staged
			</button>
		</div>

		<div v-if="showClose" class="card mt-3">
			<div class="card-body p-2">
				<div class="fw-semibold small mb-1">Close Lead with evidence</div>
				<div v-if="loadingEvidence" class="small text-muted">
					Loading evidence…
				</div>
				<div
					v-else-if="
						evidence &&
						evidence.observations.length + evidence.communications.length === 0
					"
					class="small text-muted"
				>
					No Observations or Communications are linked to this Lead's
					Assignments yet.
				</div>
				<div v-else class="small">
					<label
						v-for="candidate in candidates"
						:key="candidate.kind + candidate.id"
						class="d-flex gap-2 align-items-center"
					>
						<input
							type="checkbox"
							class="form-check-input"
							:checked="
								selectedEvidence.has(candidate.kind + ':' + candidate.id)
							"
							@change="toggleEvidence(candidate)"
						/>
						<span>{{ candidate.kind }} {{ candidate.id }}</span>
						<span class="text-muted ms-auto">{{
							formatDate(candidate.at)
						}}</span>
					</label>
				</div>
				<div class="mt-2">
					<label class="form-label small mb-1"
						>Or cite a Communication id</label
					>
					<input
						v-model="manualCommunication"
						class="form-control form-control-sm"
						placeholder="Communication id"
					/>
				</div>
				<textarea
					v-model="closeRationale"
					class="form-control form-control-sm mt-2"
					rows="2"
					placeholder="Why this answers the hypothesis"
				></textarea>
				<button
					class="btn btn-sm btn-success mt-2"
					:disabled="
						saving ||
						(selectedEvidence.size === 0 && !manualCommunication) ||
						!closeRationale.trim()
					"
					@click="closeLead"
				>
					Close Lead
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";

import type { LeadDetail, PlanningSignal } from "@/type/api";

const props = defineProps<{
	draftCells: string[];
	/** The resolution the draft's cells are cut at — the page's own, because a
	 * Lead stores one resolution and the grid decided it on the first cell. */
	resolution: number;
	evidence: { observations: any[]; communications: any[] } | null;
	lead: LeadDetail | null;
	loadingEvidence: boolean;
	saving: boolean;
	showClose: boolean;
	staged: PlanningSignal[];
}>();

const emit = defineEmits<{
	(e: "attach", role: string): void;
	(e: "remove-cell", cell: string): void;
	(e: "request-evidence"): void;
	(e: "review", signalID: number): void;
	(e: "save", payload: Record<string, unknown>): void;
	(
		e: "close",
		payload: { rationale: string; evidence: { kind: string; id: number }[] },
	): void;
}>();

const attachRole = ref("context");
const closeRationale = ref("");
const manualCommunication = ref("");
const selectedEvidence = ref(new Set<string>());
const signalEffects = reactive<Record<number, string>>({});

const form = reactive({
	name: "",
	priority: "normal",
	due: "",
	rationale: "",
});

watch(
	() => props.lead,
	(lead) => {
		form.name = lead?.name ?? "";
		form.priority = lead?.priority ?? "normal";
		form.due = lead?.due_at ? lead.due_at.slice(0, 10) : "";
		form.rationale = lead?.rationale ?? "";
		selectedEvidence.value = new Set();
		closeRationale.value = "";
		manualCommunication.value = "";
	},
	{ immediate: true },
);

watch(
	() => props.staged,
	(list) => {
		for (const signal of list) {
			if (!signalEffects[signal.id]) {
				signalEffects[signal.id] = signal.effect ?? "contextual";
			}
		}
	},
	{ immediate: true, deep: true },
);

const candidates = computed(() => [
	...(props.evidence?.observations ?? []),
	...(props.evidence?.communications ?? []),
]);

const canSave = computed(
	() => form.name.trim() !== "" && props.draftCells.length > 0,
);

/** One reason the Lead forge will refuse this draft, in the server's own order
 * (`validateLeadSignalInputs` and the production gate), with the review action
 * that would clear it when one would. */
interface DraftBlocker {
	key: string;
	message: string;
	/** The Signal to open for review, when reviewing is what clears it. */
	review?: number;
}

/**
 * What the server would reject, checked here first so the operator is told what
 * to do instead of reading a 400 after a round trip. The rules are the forge's:
 * at least one Signal, every Signal reviewed and classified, and at least one of
 * them production evidence.
 */
const draftBlockers = computed<DraftBlocker[]>(() => {
	const staged = props.staged;
	if (staged.length === 0) {
		return [
			{
				key: "no-signals",
				message:
					"A Lead needs at least one Signal: tick one in the Signals list.",
			},
		];
	}
	const blockers: DraftBlocker[] = [];
	for (const signal of staged) {
		if (signal.addressed === null || signal.addressed === undefined) {
			blockers.push({
				key: `unreviewed:${signal.id}`,
				message: `Signal ${signal.id} is not reviewed.`,
				review: signal.id,
			});
			continue;
		}
		if (
			signal.provenance_class === null ||
			signal.provenance_class === undefined
		) {
			blockers.push({
				key: `unclassified:${signal.id}`,
				message: `Signal ${signal.id} has no provenance class.`,
				review: signal.id,
			});
		}
	}
	// The production rule is only worth stating once the other two are met: an
	// unreviewed Signal has no class yet, so "no production evidence" is a
	// consequence of the review, not a second thing to do.
	const reviewed = staged.filter(
		(signal) =>
			signal.addressed !== null &&
			signal.addressed !== undefined &&
			signal.provenance_class !== null &&
			signal.provenance_class !== undefined,
	);
	if (
		blockers.length === 0 &&
		reviewed.length > 0 &&
		!reviewed.some((signal) => signal.provenance_class === "production")
	) {
		blockers.push({
			key: "no-production",
			message:
				"A Lead must include at least one Signal classed as production. Re-review one as production evidence.",
			review: reviewed[0].id,
		});
	}
	return blockers;
});

function save() {
	emit("save", {
		name: form.name.trim(),
		priority: form.priority,
		due_at: form.due ? new Date(form.due + "T00:00:00Z").toISOString() : null,
		rationale: form.rationale,
		h3_resolution: props.resolution,
		cells: props.draftCells,
		signals: props.staged.map((signal) => ({
			signal_id: signal.id,
			effect: signalEffects[signal.id] ?? "contextual",
		})),
	});
}

function attach() {
	emit("attach", attachRole.value);
}

function toggleEvidence(candidate: { kind: string; id: number }) {
	const key = `${candidate.kind}:${candidate.id}`;
	if (selectedEvidence.value.has(key)) selectedEvidence.value.delete(key);
	else selectedEvidence.value.add(key);
	selectedEvidence.value = new Set(selectedEvidence.value);
}

function closeLead() {
	const evidence = [...selectedEvidence.value].map((key) => {
		const [kind, id] = key.split(":");
		return { kind, id: Number(id) };
	});
	const manual = Number(manualCommunication.value);
	if (manual > 0) evidence.push({ kind: "communication", id: manual });
	emit("close", { rationale: closeRationale.value.trim(), evidence });
}

function reset() {
	emit("remove-cell", "");
}

function formatDate(value: string) {
	return new Date(value).toLocaleDateString();
}
</script>
